import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { formatCurrency } from '@/helpers/billing'
import { BillingSubscription, PricingModel } from '@/types/billing'
import { AppDispatch } from '@/redux'
import { useDispatch } from 'react-redux'
import { createPlaybookCheckoutSession } from '@/services/billingService'
import { showNotification } from '@/services/notificationService'
import { TCSAPIResponse } from '@/types/types'
import { BookOpen, ArrowRight } from 'lucide-react'
import { PlaybookCountControl } from './PlaybookCountControl'
import { DEFAULT_LEGACY_PLAN, LEGACY_TIER_DETAILS } from './legacyPlanDetails'
import { MAX_PLAYBOOKS, MIN_PLAYBOOKS } from '@/constants/TCS/billing'

interface PlanSelectionProps {
  token: string
  organizationId: string
  currentSubscription?: BillingSubscription | null
  onPlanSelected: () => void
}

const PLAYBOOK_PRICE_FIRST = 150
const PLAYBOOK_PRICE_ADDITIONAL = 75

function clampPlaybookCount(value: number): number {
  return Math.min(MAX_PLAYBOOKS, Math.max(MIN_PLAYBOOKS, value))
}

export const PlanSelection: React.FC<PlanSelectionProps> = ({
  token,
  organizationId,
  currentSubscription,
  onPlanSelected
}) => {
  const dispatch: AppDispatch = useDispatch()
  const pricingModel = currentSubscription?.pricingModel as PricingModel | undefined
  const isFreeQuotaOnly =
    currentSubscription?.planTier === 'free_quota_only' ||
    currentSubscription?.status === 'free_quota_only'
  const [playbookCount, setPlaybookCount] = useState(1)
  const [loadingCheckout, setLoadingCheckout] = useState(false)

  const handlePlaybookCheckout = async () => {
    const count = clampPlaybookCount(playbookCount)
    setLoadingCheckout(true)
    const resp = await dispatch(createPlaybookCheckoutSession(token, organizationId, count))
    setLoadingCheckout(false)
    if (resp.data?.url) {
      window.location.href = resp.data.url
    } else if (resp.data?.type === 'plan_changed') {
      onPlanSelected()
      showNotification(
        { data: resp.data as unknown as TCSAPIResponse, err: undefined },
        resp.data.message ?? 'Subscription updated',
        {}
      )
    } else {
      showNotification({ data: undefined, err: resp.err }, '', {})
    }
  }

  // Non-legacy: playbook-based or no subscription — no tier cards
  if (pricingModel === 'playbook_based_v2' || !pricingModel) {
    // Has playbook-based subscription: show manage count inline
    if (currentSubscription && pricingModel === 'playbook_based_v2') {
      return (
        <PlaybookCountControl
          token={token}
          organizationId={organizationId}
          subscription={currentSubscription}
          onUpdated={onPlanSelected}
        />
      )
    }

    // No subscription, free quota only, or legacy (migration): show playbook checkout — CTA front and center at top
    const totalMonthly =
      playbookCount <= 1
        ? PLAYBOOK_PRICE_FIRST
        : PLAYBOOK_PRICE_FIRST + (playbookCount - 1) * PLAYBOOK_PRICE_ADDITIONAL

    const isFirstTime = !currentSubscription || isFreeQuotaOnly
    const heading = isFirstTime ? 'Get Started' : 'Set up billing'
    const subtext = currentSubscription && !isFreeQuotaOnly
      ? 'Switch to pay-per-playbook. Your current plan will be canceled and your new subscription will start.'
      : 'Choose how many playbooks you need. You can change this later.'
    const buttonLabel = isFirstTime ? 'Get Started' : 'Move to playbook-based pricing'

    return (
      <div id="plan-selection" className="px-6 py-5">
        <h2 className="text-center text-2xl font-bold text-gray-900">
          {heading}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {subtext}
        </p>

        <div className="mt-6 space-y-4">
          <div>
            <Label htmlFor="playbook-count-checkout" className="text-gray-700">
              Number of playbooks
            </Label>
            <input
              id="playbook-count-checkout"
              type="number"
              min={MIN_PLAYBOOKS}
              max={MAX_PLAYBOOKS}
              value={playbookCount}
              onChange={(e) =>
                setPlaybookCount(clampPlaybookCount(Number(e.target.value) || MIN_PLAYBOOKS))
              }
              className="mt-2 h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
            <p className="text-sm font-medium text-gray-700">
              {formatCurrency(PLAYBOOK_PRICE_FIRST)} for the first playbook, {formatCurrency(PLAYBOOK_PRICE_ADDITIONAL)}/month for each additional
            </p>
            <p className="mt-1 text-lg font-semibold text-gray-900">
              {formatCurrency(totalMonthly)}/month
            </p>
          </div>
          <Button
            onClick={handlePlaybookCheckout}
            disabled={loadingCheckout}
            className="w-full"
          >
            {loadingCheckout ? (
              <span className="flex items-center gap-2">
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                Processing…
              </span>
            ) : (
              <>
                {buttonLabel}
                {currentSubscription && !isFreeQuotaOnly ? (
                  <ArrowRight className="ml-2 h-4 w-4" />
                ) : (
                  <BookOpen className="ml-2 h-4 w-4" />
                )}
              </>
            )}
          </Button>
        </div>

        <p className="mt-6 text-center text-xs text-gray-500">
          You can cancel anytime from the Customer Portal.
        </p>
      </div>
    )
  }

  // Legacy (plan_tier_based_v1): show current plan + encourage move to playbook-based
  const tierKey = (currentSubscription?.planTier ?? 'starter').toString().toLowerCase()
  const plan = LEGACY_TIER_DETAILS[tierKey] ?? DEFAULT_LEGACY_PLAN

  return (
    <div id="plan-selection" className="px-6 py-5">
      <h2 className="mb-2 text-xl font-semibold text-gray-900">Your plan</h2>
      <p className="mb-4 text-sm text-gray-600">
        You’re on a legacy plan. We recommend moving to playbook-based pricing for more flexibility.
      </p>
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <p className="font-medium text-gray-900">
          {plan.name} Plan — {formatCurrency(plan.price)}/month
        </p>
        <p className="mt-1 text-sm text-gray-600">
          {plan.credentials} credentials included
        </p>
      </div>
      <Button
        onClick={handlePlaybookCheckout}
        disabled={loadingCheckout}
        variant="default"
        className="mt-4 w-full"
      >
        {loadingCheckout ? (
          <span className="flex items-center gap-2">
            <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            Processing…
          </span>
        ) : (
          <>
            Move to playbook-based pricing
            <ArrowRight className="ml-2 h-4 w-4" />
          </>
        )}
      </Button>
      <p className="mt-4 text-center text-xs text-gray-500">
        Your current plan will be canceled and your new subscription will start. You can also keep your current plan and manage it in the Customer Portal.
      </p>
    </div>
  )
}
